import React, { useEffect, useState, useMemo } from "react";
import { FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Button, Modal, Input, message } from "antd";
import { formatDateByLang } from "../../function/formatDate";
import PartnerApi from "../../../helpers/PartnerApi";
import ModalNoteDelete from "../../../Modal/ModalNoteDelete";
import { DefaultButton, GrayButton } from "../../../components/buttons";
import { useDispatch } from "react-redux";
import { info as toastInfo, error as toastError, success } from "../../../features/toast/toastSlice";

function useIsDark() {
  const [isDark, setIsDark] = useState(
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
  );

  useEffect(() => {
    if (typeof document === "undefined") return;
    const el = document.documentElement;
    const obs = new MutationObserver(() =>
      setIsDark(el.classList.contains("dark"))
    );
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  return isDark;
}

const CustomCheckbox = ({
  checked,
  label,
  disabled,
  onChange,
  isDark,
  variant = "list",
}) => (
  <label className="inline-flex items-center space-x-2 cursor-pointer select-none">
    <span
      role="checkbox"
      aria-checked={checked}
      className={`
        relative inline-flex items-center justify-center
        h-5 w-5 rounded-[4px]
        transition-all
        ${variant === "modal"
          ? `border ${checked ? "bg-white border-gray-300" : "bg-white border-gray-300"
          }`
          : `${checked
            ? "bg-[#6f6577] border border-[#6f6577]"
            : "bg-transparent border-transparent"
          }`
        }
        ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
      `}
      style={{ flex: "0 0 auto" }}
      onClick={!disabled ? () => onChange?.(!checked) : undefined}
    >
      {checked && (
        <svg
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
          className="h-3.5 w-3.5"
        >
          <path
            d="M3 8.5 L6.5 12 L13 5.5"
            fill="none"
            stroke={variant === "modal" ? "#0d72ca" : "#ffffff"}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>

    {label && (
      <span className={`${isDark ? "text-white" : "text-gray-800"}`}>
        {label}
      </span>
    )}
  </label>
);

const NoteList = ({ data, handleDelete, handleAdd, licenseId }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isDark = useIsDark();

  // ✅ licenseId resolve: prop → data.licenseId → data.id
  const resolvedLicenseId = useMemo(() => {
    const fromProp = licenseId ?? null;
    const fromData = data?.licenseId ?? data?.id ?? null;
    return fromProp || fromData;
  }, [licenseId, data]);

  // ✅ local list state
  const [localList, setLocalList] = useState(() =>
    Array.isArray(data?.noteList) ? data.noteList : []
  );

  useEffect(() => {
    setLocalList(Array.isArray(data?.noteList) ? data.noteList : []);
  }, [data?.noteList]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [isAkead, setIsAkead] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deleteModalNoteOpen, setDeleteModalNoteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ✅ Toast helpers (sadece işlem sonucunda)
  const notifySuccess = (msg) =>
    dispatch(success({ message: t(msg), delay: 4000 }));
  const notifyInfo = (msg) =>
    dispatch(toastInfo({ title: t("Info"), message: t(msg), delay: 4000 }));
  const notifyErr = (msg) =>
    dispatch(
      toastError({
        title: t("Error"),
        message: String(msg || t("ErrorOccurred")),
        delay: 6000,
      })
    );

  const openAddModal = () => {
    setNewNote("");
    setIsAkead(false);
    setIsModalOpen(true);
  };
  const closeAddModal = () => setIsModalOpen(false);

  const saveNote = async () => {
    const noteText = newNote.trim();
    if (!noteText) return notifyInfo("Please enter a note.");
    if (!resolvedLicenseId) return notifyErr(t("License ID is missing."));

    try {
      setSaving(true);

      const res = await PartnerApi.postLicenseNote({
        licenseId: resolvedLicenseId,
        note: noteText,
        internal: isAkead ? 1 : 0,
      });

      const createdId =
        res?.licenseNoteId ?? res?.licenseNote?.id ?? res?.id ?? Date.now();

      const newItem = {
        id: createdId,
        note: noteText,
        internal: isAkead ? 1 : 0,
        createdAt: new Date().toISOString(),
        createdUser: "-",
      };

      // ✅ tabloda anında göster
      setLocalList((prev) => [newItem, ...(Array.isArray(prev) ? prev : [])]);
      handleAdd?.(newItem);

      setIsModalOpen(false);
      notifySuccess("Successfuly saved");
    } catch (err) {
      notifyErr(
        err?.response?.data?.error?.message ||
        err?.response?.data?.error?.more ||
        err?.message ||
        t("An error occurred while saving the note.")
      );
    } finally {
      setSaving(false);
    }
  };

  // ✅ DELETE’e tıklayınca sadece modal açılır (submit/bubble yok)
  const confirmDelete = (e, id) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setDeleteId(id);
    setDeleteModalNoteOpen(true);
  };

  // ✅ sadece modal içindeki Delete butonu tıklanınca API çağrısı
  const onDeleteNoteClick = async () => {
    const idToDelete = deleteId;
    if (!idToDelete || deleting) return;

    try {
      setDeleting(true);

      // ✅ önce API
      await PartnerApi.deleteLicenseNote(idToDelete);

      // ✅ başarılıysa listeden kaldır
      setLocalList((prev) =>
        Array.isArray(prev)
          ? prev.filter((n) => {
            const nid = n.id ?? n.licenseNoteId ?? n.noteId ?? n._id;
            return String(nid) !== String(idToDelete);
          })
          : prev
      );

      handleDelete?.(idToDelete);
      notifySuccess("Successfully Deleted");
    } catch (err) {
      notifyErr(
        err?.response?.data?.error?.message ||
        err?.response?.data?.error?.more ||
        err?.message ||
        t("An error occurred while deleting the note.")
      );
    } finally {
      setDeleting(false);
      setDeleteModalNoteOpen(false);
      setDeleteId(null);
    }
  };

  return (
    <div className="mt-3 pt-2">
      {/* ADD MODAL */}
      <Modal
        open={isModalOpen}
        footer={null}
        onCancel={closeAddModal}
        title={t("Add New Note")}
        closeIcon={
          <span
            className={isDark ? "text-white" : "text-gray-700"}
            style={{ fontSize: 18, lineHeight: 1 }}
          >
            ✕
          </span>
        }
        className="
          [&_.ant-modal-content]:bg-white
          [&_.ant-modal-header]:bg-white
          [&_.ant-modal-title]:text-gray-800
          dark:[&_.ant-modal-content]:bg-gray-900
          dark:[&_.ant-modal-header]:bg-gray-900
          dark:[&_.ant-modal-title]:text-gray-100
        "
      >
        <div className="flex flex-col gap-y-2 mt-4">
          <Input.TextArea
            rows={4}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder={t("Enter your note here")}
            className="
              text-black placeholder-gray-500
              bg-white border border-gray-300
              focus:ring-0
              dark:text-black dark:placeholder-gray-500
              dark:bg-white dark:border-gray-400
            "
          />

          <div className="mt-3">
            <CustomCheckbox
              checked={isAkead}
              onChange={setIsAkead}
              label={t("Akead")}
              isDark={isDark}
              variant="modal"
            />
          </div>

          <div className="flex justify-between mt-4">
            <GrayButton type="button" onClick={closeAddModal} disabled={saving}>
              {t("Cancel")}
            </GrayButton>

            <DefaultButton
              type="button"
              disabled={saving || newNote.trim() === ""}
              onClick={saveNote}
            >
              {saving ? t("Loading") : t("Save")}
            </DefaultButton>
          </div>
        </div>
      </Modal>

      {/* DELETE CONFIRM MODAL */}
      <ModalNoteDelete
        open={deleteModalNoteOpen}
        setOpen={setDeleteModalNoteOpen}
        onDeleteClick={onDeleteNoteClick}
        confirmLoading={deleting}
        title={t("Delete Note")}
        text={
          <div className="text-xl font-semibold">
            {t("Are you sure you want to delete this note?")}
          </div>
        }
      />

      {/* TABLE */}
      {localList.length > 0 ? (
        <div className="w-full px-2 sm:px-4 md:px-7">
          <div className="w-full flex mb-2">
            <Button
              style={{ backgroundColor: "#0d72ca", color: "white", border: "none" }}
              className="ml-auto whitespace-nowrap flex-shrink-0"
              onClick={openAddModal}
            >
              {t("Add")}
            </Button>
          </div>

          <div className="w-full overflow-x-auto">
            <table className="min-w-[780px] w-full border-collapse mt-5">
              <thead className="flex justify-around dark:text-white font-semibold">
                <tr className="flex w-full">
                  <td className="w-[160px] text-center border dark:border-gray-400 border-gray-300 px-2 py-2">
                    {t("Date")}
                  </td>
                  <td className="flex-1 text-center border dark:border-gray-400 border-gray-300 px-2 py-2">
                    {t("Notes")}
                  </td>
                  <td className="w-[180px] text-center border dark:border-gray-400 border-gray-300 px-2 py-2">
                    {t("Name")}
                  </td>
                  <td className="w-[110px] text-center border dark:border-gray-400 border-gray-300 px-2 py-2">
                    {t("Akead")}
                  </td>
                  <td className="w-[110px] text-center border dark:border-gray-400 border-gray-300 px-2 py-2">
                    {t("Delete")}
                  </td>
                </tr>
              </thead>

              <tbody className="flex flex-col bg-white h-full text-black dark:bg-gray-600 dark:text-white">
                {localList.map((note, index) => {
                  const id =
                    note.id ?? note.licenseNoteId ?? note.noteId ?? note._id ?? index;

                  const internalVal =
                    typeof note.internal === "boolean"
                      ? note.internal
                      : Number(note.internal) === 1;

                  const dateText = note.createdAt
                    ? formatDateByLang(note.createdAt, i18n.language)
                    : "-";

                  const nameText = note.createdUser || note.name || "-";
                  const noteText = note.note || "-";

                  return (
                    <tr className="flex w-full" key={id}>
                      <td className="w-[160px] text-center border dark:border-gray-400 border-gray-300 px-2 py-2">
                        <div className="break-words">{dateText}</div>
                      </td>

                      <td className="flex-1 text-left border dark:border-gray-400 border-gray-300 px-2 py-2 min-w-0">
                        <div className="break-words whitespace-normal">{noteText}</div>
                      </td>

                      <td className="w-[180px] text-left border dark:border-gray-400 border-gray-300 px-2 py-2">
                        <div className="break-words">{nameText}</div>
                      </td>

                      <td className="w-[110px] text-center border dark:border-gray-400 border-gray-300 px-2 py-2">
                        <div className="flex justify-center">
                          <CustomCheckbox
                            checked={internalVal}
                            disabled
                            isDark={isDark}
                            variant="list"
                          />
                        </div>
                      </td>

                      <td className="w-[110px] text-center border dark:border-gray-400 border-gray-300 px-2 py-2">
                        <button
                          type="button"
                          onClick={(e) => confirmDelete(e, id)}
                          className="text-red-600 hover:text-red-800 dark:text-red-300 dark:hover:text-red-200"
                          title={t("Delete")}
                        >
                          <FaTimes className="w-4 h-4 inline-block" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:!text-white">
          {t("No note found.")}
        </p>
      )}

    </div>
  );
};

export default NoteList;